import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  collection,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from './firebase';
import { generateRoomCode } from '../utils/generateCode';

/**
 * Menti Session Service
 * Handles live Menti session lifecycle + slide navigation
 */

export const createMentiSession = async (presentationId) => {
  try {
    let roomCode;
    const maxAttempts = 10;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      roomCode = generateRoomCode();
      const q = query(collection(db, 'mentiSessions'), where('id', '==', roomCode));
      const existing = await getDocs(q);
      if (existing.empty) break;
      if (attempt === maxAttempts - 1) {
        throw new Error('Klarte ikkje generere unik PIN-kode. Prøv igjen.');
      }
    }

    const sessionRef = doc(db, 'mentiSessions', roomCode);
    const session = {
      id: roomCode,
      presentationId,
      status: 'waiting',
      createdAt: new Date().toISOString(),
      startedAt: null,
      finishedAt: null,
      currentSlideIndex: 0,
      participants: {},
      responses: {}
    };

    await setDoc(sessionRef, session);
    return session;
  } catch (error) {
    console.error('Error creating menti session:', error);
    throw new Error(`Klarte ikkje opprette Menti-økt: ${error.message}`);
  }
};

export const startMentiSession = async (sessionId) => {
  try {
    const ref = doc(db, 'mentiSessions', sessionId);
    await updateDoc(ref, {
      status: 'active',
      startedAt: new Date().toISOString(),
      currentSlideIndex: 0
    });
  } catch (error) {
    console.error('Error starting menti session:', error);
    throw new Error(`Klarte ikkje starte Menti-økt: ${error.message}`);
  }
};

export const endMentiSession = async (sessionId) => {
  try {
    const ref = doc(db, 'mentiSessions', sessionId);
    await updateDoc(ref, {
      status: 'finished',
      finishedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error ending menti session:', error);
    throw new Error(`Klarte ikkje avslutte Menti-økt: ${error.message}`);
  }
};

export const getMentiSession = async (sessionId) => {
  try {
    const ref = doc(db, 'mentiSessions', sessionId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Fann ikkje Menti-økta');
    return snap.data();
  } catch (error) {
    console.error('Error fetching menti session:', error);
    throw new Error(`Klarte ikkje hente Menti-økt: ${error.message}`);
  }
};

export const deleteMentiSession = async (sessionId) => {
  await deleteDoc(doc(db, 'mentiSessions', sessionId));
};

export const advanceSlide = async (sessionId, totalSlides, direction = 1) => {
  try {
    const ref = doc(db, 'mentiSessions', sessionId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Fann ikkje Menti-økta');

    const current = snap.data().currentSlideIndex || 0;
    const next = Math.max(0, Math.min(totalSlides - 1, current + direction));

    await updateDoc(ref, { currentSlideIndex: next });
    return next;
  } catch (error) {
    console.error('Error advancing slide:', error);
    throw new Error(`Klarte ikkje byte lysbilde: ${error.message}`);
  }
};

export const goToSlide = async (sessionId, index) => {
  try {
    const ref = doc(db, 'mentiSessions', sessionId);
    await updateDoc(ref, { currentSlideIndex: index });
  } catch (error) {
    console.error('Error going to slide:', error);
    throw new Error(`Klarte ikkje gå til lysbilde: ${error.message}`);
  }
};
